import {Label, Slider, TextInput} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

interface SourcingSlider2Props extends TestIdProps {
  label?: string;
  labelTooltip?: string;
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
  step?: number;
  value?: [number, number];
  onChange: (value: [number, number]) => void;
}

export function SourcingSlider2(props: SourcingSlider2Props) {
  const defaultMin = props.value?.[0] ?? props.min;
  const defaultMax = props.value?.[1] ?? props.max;
  const [inputMinValue, setInputMinValue] = useState<string | null>(defaultMin.toString());
  const [inputMaxValue, setInputMaxValue] = useState<string | null>(defaultMax.toString());
  const [isInputMinFocused, setIsInputMinFocused] = useState(false);
  const [isInputMaxFocused, setIsInputMaxFocused] = useState(false);

  const putValueInRange = (value: number) => Math.min(Math.max(value, props.min), props.max);

  const switchValuesIfNeeded = () => {
    if (
      isNotNil(inputMinValue) &&
      isNotNil(inputMaxValue) &&
      parseInt(inputMinValue) > parseInt(inputMaxValue)
    ) {
      setInputMinValue(inputMaxValue);
      setInputMaxValue(inputMinValue);
      props.onChange([defaultMax, defaultMin]);
    }
  };

  const formatValue = (value: number) => `${value} %`;

  const handleInputMinChange = (value: string | null) => {
    setInputMinValue(value);

    const parsedValue = isNotNil(value) ? parseInt(value) : NaN;
    props.onChange([isNaN(parsedValue) ? props.min : putValueInRange(parsedValue), defaultMax]);
  };

  const handleInputMinFocus = () => {
    setIsInputMinFocused(true);
    setInputMinValue(defaultMin.toString());
  };

  const handleInputMinBlur = () => {
    setIsInputMinFocused(false);
    switchValuesIfNeeded();
  };

  const handleInputMaxChange = (value: string | null) => {
    setInputMaxValue(value);

    const parsedValue = isNotNil(value) ? parseInt(value) : NaN;
    props.onChange([defaultMin, isNaN(parsedValue) ? props.max : putValueInRange(parsedValue)]);
  };

  const handleInputMaxFocus = () => {
    setIsInputMaxFocused(true);
    setInputMaxValue(defaultMax.toString());
  };

  const handleInputMaxBlur = () => {
    setIsInputMaxFocused(false);
    switchValuesIfNeeded();
  };

  return (
    <VStack>
      <HStack height={9} justify="space-between">
        <Label tooltip={props.labelTooltip}>{props.label}</Label>
        <HStack spacing={2} align="center" width={34}>
          <TextInput
            type={isInputMinFocused ? 'number' : 'text'}
            value={isInputMinFocused ? inputMinValue : formatValue(defaultMin)}
            onChange={handleInputMinChange}
            onFocus={handleInputMinFocus}
            onBlur={handleInputMinBlur}
            data-testid={suffixTestId('sliderMinInput', props)}
          />
          –
          <TextInput
            type={isInputMaxFocused ? 'number' : 'text'}
            value={isInputMaxFocused ? inputMaxValue : formatValue(defaultMax)}
            onChange={handleInputMaxChange}
            onFocus={handleInputMaxFocus}
            onBlur={handleInputMaxBlur}
            data-testid={suffixTestId('sliderMaxInput', props)}
          />
        </HStack>
      </HStack>
      <Box paddingVertical={1}>
        <Slider
          isRange
          isRainbow
          allowMoveStart
          ticks={[
            {value: 0, label: props.minLabel},
            {value: 100, label: props.maxLabel},
          ]}
          step={props.step ?? 1}
          min={props.min}
          max={props.max}
          value={props.value || [props.min, props.max]}
          onChange={props.onChange}
          data-testid={suffixTestId('slider', props)}
        />
      </Box>
    </VStack>
  );
}
